import { Params } from '@angular/router';
import { AudienceFilter, CareerFilter } from '../../../components/artist-filter/artist-filter.component';

export type ArtistSortOption =
  | 'rank' | 'globalRank' | 'hypeScore' | 'name' | 'spotify' | 'youtube' | 'instagram' | 'tiktok'
  | 'totalSongs' | 'activeSince' | 'avgTrackLength' | 'chartedSongs';
export type ArtistFilterTrend = 'all' | 'up' | 'down' | 'breakout';
export type ArtistBreadthTimeframe = '24h' | '7d' | '15d' | '30d';
export type ArtistDatePreset = 'this_month';

export interface ArtistGridUrlState {
  columns: string[];
  hiddenColumns: string[];
  hiddenKpis: string[];
  audienceFilters: AudienceFilter[];
  careerFilters: CareerFilter[];
  trend: ArtistFilterTrend;
  search: string;
  sortOption: ArtistSortOption;
  sortDirection: 'asc' | 'desc';
  pageSize: number;
  currentPage: number;
  breadthTimeframe: ArtistBreadthTimeframe;
  startDate: string | null;
  endDate: string | null;
  datePreset?: ArtistDatePreset | null;
}

export const ARTIST_GRID_URL_VERSION = '1';
export const VIEW_URL_PARAM = 'view';

export function viewUrlSlug(view: { id: number; name: string }): string {
  const name = view.name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  return name ? `${view.id}-${name}` : String(view.id);
}

export function parseViewUrlParam(value: string | string[] | null | undefined): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  const match = raw ? /^(\d+)/.exec(raw) : null;
  return match ? Number(match[1]) : null;
}

export function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function thisMonthRange(now = new Date()): { startDate: Date; endDate: Date } {
  return {
    startDate: new Date(now.getFullYear(), now.getMonth(), 1),
    endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
  };
}

export function timeframeForRange(startDate: Date, endDate: Date): ArtistBreadthTimeframe {
  const diffDays = Math.ceil(Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return '24h';
  if (diffDays <= 10) return '7d';
  if (diffDays <= 20) return '15d';
  return '30d';
}

const AUDIENCE_FILTER_VALUES: AudienceFilter[] = ['rising', 'established', 'household'];
const CAREER_FILTER_VALUES: CareerFilter[] = ['newcomer', 'building', 'veteran'];
const TREND_VALUES: ArtistFilterTrend[] = ['all', 'up', 'down', 'breakout'];
const TIMEFRAME_VALUES: ArtistBreadthTimeframe[] = ['24h', '7d', '15d', '30d'];
const SORT_DIRECTION_VALUES: Array<'asc' | 'desc'> = ['asc', 'desc'];

function splitParam(value: string | null | undefined): string[] {
  if (!value) return [];
  return value.split(',').filter((v) => v.length > 0);
}

function pickEnum<T extends string>(value: string | null | undefined, allowed: T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function pickIntInRange(value: string | null | undefined, min: number, fallback: number): number {
  const n = value != null ? Number.parseInt(value, 10) : NaN;
  return Number.isFinite(n) && n >= min ? n : fallback;
}

export function encodeArtistGridState(state: ArtistGridUrlState, defaults: ArtistGridUrlState): Params {
  const params: Params = {};

  const arraysDiffer = (a: string[], b: string[]) => a.length !== b.length || a.some((v, i) => v !== b[i]);
  const setsdiffer = (a: string[], b: string[]) => {
    const sa = [...a].sort();
    const sb = [...b].sort();
    return arraysDiffer(sa, sb);
  };

  if (arraysDiffer(state.columns, defaults.columns)) params['cols'] = state.columns.join(',');
  if (setsdiffer(state.hiddenColumns, defaults.hiddenColumns)) params['hidden'] = state.hiddenColumns.join(',');
  if (setsdiffer(state.hiddenKpis, defaults.hiddenKpis)) params['kpis'] = state.hiddenKpis.join(',');
  if (setsdiffer(state.audienceFilters, defaults.audienceFilters)) params['aud'] = state.audienceFilters.join(',');
  if (setsdiffer(state.careerFilters, defaults.careerFilters)) params['career'] = state.careerFilters.join(',');
  if (state.trend !== defaults.trend) params['trend'] = state.trend;
  if (state.search.trim() !== defaults.search.trim()) params['q'] = state.search.trim();
  if (state.sortOption !== defaults.sortOption) params['sort'] = state.sortOption;
  if (state.sortDirection !== defaults.sortDirection) params['dir'] = state.sortDirection;
  if (state.pageSize !== defaults.pageSize) params['size'] = String(state.pageSize);
  if (state.currentPage !== defaults.currentPage) params['page'] = String(state.currentPage);
  if ((state.datePreset ?? null) !== (defaults.datePreset ?? null)) params['dp'] = state.datePreset ?? 'none';
  if (!state.datePreset) {
    if (state.breadthTimeframe !== defaults.breadthTimeframe) params['tf'] = state.breadthTimeframe;
    if (state.startDate !== defaults.startDate || state.endDate !== defaults.endDate) {
      params['from'] = state.startDate ?? 'none';
      params['to'] = state.endDate ?? 'none';
    }
  }

  if (Object.keys(params).length > 0) {
    params['v'] = ARTIST_GRID_URL_VERSION;
  }

  return params;
}

export function decodeArtistGridState(
  params: Record<string, string | string[] | null | undefined>,
  defaults: ArtistGridUrlState
): ArtistGridUrlState {
  const get = (key: string): string | null => {
    const raw = params[key];
    if (Array.isArray(raw)) return raw[0] ?? null;
    return raw ?? null;
  };

  const cols = get('cols');
  const hidden = get('hidden');
  const kpis = get('kpis');
  const aud = get('aud');
  const career = get('career');
  const from = get('from');
  const to = get('to');
  const hasCustomRange = from !== null && to !== null && parseLocalDate(from) !== null && parseLocalDate(to) !== null;
  const clearsRange = from === 'none' || to === 'none';
  const dp = get('dp');
  const datePreset: ArtistDatePreset | null =
    dp === 'this_month' ? 'this_month' : dp === 'none' ? null : (defaults.datePreset ?? null);

  return {
    columns: cols !== null ? splitParam(cols) : defaults.columns,
    hiddenColumns: hidden !== null ? splitParam(hidden) : defaults.hiddenColumns,
    hiddenKpis: kpis !== null ? splitParam(kpis) : defaults.hiddenKpis,
    audienceFilters: aud !== null ? (splitParam(aud).filter((v) => AUDIENCE_FILTER_VALUES.includes(v as AudienceFilter)) as AudienceFilter[]) : defaults.audienceFilters,
    careerFilters: career !== null ? (splitParam(career).filter((v) => CAREER_FILTER_VALUES.includes(v as CareerFilter)) as CareerFilter[]) : defaults.careerFilters,
    trend: pickEnum(get('trend'), TREND_VALUES, defaults.trend),
    search: get('q') ?? defaults.search,
    sortOption: pickEnum(get('sort'), ['rank', 'globalRank', 'hypeScore', 'name', 'spotify', 'youtube', 'instagram', 'tiktok', 'totalSongs', 'activeSince', 'avgTrackLength', 'chartedSongs'] as ArtistSortOption[], defaults.sortOption),
    sortDirection: pickEnum(get('dir'), SORT_DIRECTION_VALUES, defaults.sortDirection),
    pageSize: pickIntInRange(get('size'), 1, defaults.pageSize),
    currentPage: pickIntInRange(get('page'), 1, defaults.currentPage),
    breadthTimeframe: datePreset ? defaults.breadthTimeframe : pickEnum(get('tf'), TIMEFRAME_VALUES, defaults.breadthTimeframe),
    startDate: datePreset || clearsRange ? null : hasCustomRange ? from : defaults.startDate,
    endDate: datePreset || clearsRange ? null : hasCustomRange ? to : defaults.endDate,
    datePreset,
  };
}

export function isArtistGridStateEqual(a: ArtistGridUrlState, b: ArtistGridUrlState): boolean {
  const sameOrderedArray = (x: string[], y: string[]) => x.length === y.length && x.every((v, i) => v === y[i]);
  const sameSet = (x: string[], y: string[]) => {
    if (x.length !== y.length) return false;
    const sx = [...x].sort();
    const sy = [...y].sort();
    return sx.every((v, i) => v === sy[i]);
  };

  return (
    sameOrderedArray(a.columns, b.columns) &&
    sameSet(a.hiddenColumns, b.hiddenColumns) &&
    sameSet(a.hiddenKpis, b.hiddenKpis) &&
    sameSet(a.audienceFilters, b.audienceFilters) &&
    sameSet(a.careerFilters, b.careerFilters) &&
    a.trend === b.trend &&
    a.search.trim() === b.search.trim() &&
    a.sortOption === b.sortOption &&
    a.sortDirection === b.sortDirection &&
    a.pageSize === b.pageSize &&
    a.currentPage === b.currentPage &&
    isSamePeriod(a, b)
  );
}

function isSamePeriod(a: ArtistGridUrlState, b: ArtistGridUrlState): boolean {
  const presetA = a.datePreset ?? null;
  const presetB = b.datePreset ?? null;
  if (presetA !== presetB) return false;
  if (presetA) return true;
  return a.breadthTimeframe === b.breadthTimeframe && a.startDate === b.startDate && a.endDate === b.endDate;
}
